import { FollowButtonOutline, FollowButtonVariant, theme, useResponsive } from "@product/scmp-sdk";
import isEmpty from "lodash/isEmpty";
import type { FunctionComponent } from "react";
import { useState } from "react";
import { graphql, useFragment } from "react-relay";

import { EntityFollowButton } from "~/components/entity-follow-button";
import { ZIndexLayer } from "~/lib/styles";
import type { topicHeaderTopic$key } from "~/queries/__generated__/topicHeaderTopic.graphql";

import {
  Background,
  Container,
  Content,
  Description,
  DrawerCardContent,
  ImageIcon,
  ImageIconContainer,
  Info,
  Label,
  RelatedLinksContainer,
  RelatedLinksDivider,
  StyledDrawer,
  StyledRelatedLinks,
  StyledSponsorTag,
  Title,
  TitleContainer,
} from "./styles";

type Props = {
  className?: string;
  reference: topicHeaderTopic$key;
};

export const TopicHeader: FunctionComponent<Props> = ({ className, reference: reference_ }) => {
  const topic = useFragment(
    graphql`
      fragment topicHeaderTopic on Topic {
        ...topicHeaderRelatedLinksTopic
        ...entityFollowButtonBase
        description {
          html
        }
        images(filter: { type: { values: [MOBILE_APP_IMAGE, BREAKOUT, BREAKOUT_SPONSOR] } }) {
          type
          icon: style(filter: { style: "118x118" }) {
            url
          }
          breakout: style(filter: { style: "2000x792" }) {
            url
          }
          breakoutSponsor: style(filter: { style: "1560x218" }) {
            url
          }
        }
        name
        sponsor {
          ...sponsorTagSponsor
        }
      }
    `,
    reference_,
  );

  const isMobileView = useResponsive(theme.breakpoints.only("mobile"), false);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleDrawerCloseClick = () => setIsDrawerOpen(false);
  const handleDrawerOpenClick = () => setIsDrawerOpen(true);

  const breakoutSponsorImage = topic.images?.find(image => image?.type === "breakout_sponsor");
  const breakoutImage = topic.images?.find(image => image?.type === "breakout");
  const mobileAppImage = topic.images?.find(image => image?.type === "mobileAppImage");

  const backgroundImageUrl =
    breakoutSponsorImage?.breakoutSponsor?.url ?? breakoutImage?.breakout?.url;
  const backgroundImageAspectRatio = breakoutSponsorImage?.breakoutSponsor?.url
    ? "1560 / 218"
    : "2000 / 792";

  const iconImageUrl = mobileAppImage?.icon?.url;

  const computeFollowButtonVariant = (): FollowButtonVariant => {
    if (!isMobileView && !isEmpty(backgroundImageUrl)) return FollowButtonVariant.Dark;
    return FollowButtonVariant.Light;
  };

  const followButtonVariant = computeFollowButtonVariant();

  const sponsorColorMap: Record<FollowButtonVariant, string> = {
    dark: "#fff",
    light: "#001246",
  };

  return (
    <Container className={className}>
      {backgroundImageUrl && (
        <Background
          $aspectRatio={backgroundImageAspectRatio}
          $objectFit="cover"
          lazyload={false}
          src={backgroundImageUrl}
        />
      )}

      <Content $hasBackgroundImage={!!backgroundImageUrl}>
        <Label>Topic</Label>

        <TitleContainer>
          <Title>
            {topic.name}
            {topic.description?.html && <Info onClick={handleDrawerOpenClick}>i</Info>}
          </Title>

          {iconImageUrl && (
            <ImageIconContainer>
              <ImageIcon $aspectRatio="1" lazyload={false} src={iconImageUrl} />
            </ImageIconContainer>
          )}
        </TitleContainer>
        <EntityFollowButton
          outline={FollowButtonOutline.Outlined}
          reference={topic}
          variant={followButtonVariant}
        />
        {topic.description?.html && (
          <Description dangerouslySetInnerHTML={{ __html: topic.description?.html }} />
        )}

        {topic.sponsor && (
          <StyledSponsorTag
            color={sponsorColorMap[followButtonVariant]}
            gap="5px"
            imageVariant="large"
            layoutDirection="vertical"
            nameFontSize="14px"
            reference={topic.sponsor}
            typeFontSize="12px"
          />
        )}
      </Content>

      <RelatedLinksContainer>
        <StyledRelatedLinks reference={topic} />
      </RelatedLinksContainer>
      <RelatedLinksDivider />

      <StyledDrawer
        anchor="bottom"
        open={isDrawerOpen && isMobileView}
        PaperProps={{
          sx: {
            backgroundColor: "rgba(244, 244, 240, 0.9)",
          },
        }}
        sx={{
          zIndex: `${ZIndexLayer.Drawer}`,
        }}
        title={topic.name}
        withClose
        onClose={handleDrawerCloseClick}
      >
        {topic.description?.html && (
          <DrawerCardContent dangerouslySetInnerHTML={{ __html: topic.description?.html }} />
        )}
      </StyledDrawer>
    </Container>
  );
};

TopicHeader.displayName = "TopicHeader";
